import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldAlert,
  Search,
  Plus,
  X,
  CheckCircle2,
  AlertTriangle,
  UserCheck,
  UserX,
  Eye,
  ScanEye,
  Trash2,
  ScrollText,
} from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dot,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { listFunds, type Fund } from "@/lib/api/funds";
import {
  addRestrictedEntry,
  listRestrictedEntries,
  listWallEvents,
  recordWallEvent,
  removeRestrictedEntry,
  type EntrySource,
  type ListType,
  type RestrictedEntryRow,
  type WallEventType,
} from "@/lib/api/mnpi";
import { screenName, wallLogStats, type ScreenResult } from "@argus/engine";
import { cn } from "@/lib/utils";

const LIST_TYPE_LABEL: Record<ListType, string> = { restricted: "Restricted", watch: "Watch" };
const LIST_TYPE_TONE: Record<ListType, "breach" | "risk"> = {
  restricted: "breach",
  watch: "risk",
};

const SOURCE_LABEL: Record<EntrySource, string> = {
  board_seat: "Board seat",
  deal_diligence: "Deal diligence",
  nda: "NDA",
  advisory_role: "Advisory role",
  extraction_flag: "Flagged from extraction",
  manual: "Manual",
  other: "Other",
};

const EVENT_TYPE_LABEL: Record<WallEventType, string> = {
  wall_crossing_granted: "Granted",
  wall_crossing_denied: "Denied",
  access_review: "Access review",
  list_screen: "List screen",
};

const EVENT_TYPE_TONE: Record<WallEventType, "clear" | "breach" | "info" | "neutral"> = {
  wall_crossing_granted: "clear",
  wall_crossing_denied: "breach",
  access_review: "info",
  list_screen: "neutral",
};

const EVENT_TYPE_ICON: Record<WallEventType, React.ComponentType<{ className?: string }>> = {
  wall_crossing_granted: UserCheck,
  wall_crossing_denied: UserX,
  access_review: Eye,
  list_screen: ScanEye,
};

const inputCls =
  "mt-1 w-full rounded-md border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-sm text-neutral-200 placeholder:text-neutral-600";

function RestrictedListInner() {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { role, canWrite } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";
  const canLog = role !== null; // any accepted tenant member may log a wall-crossing event

  const [screenQuery, setScreenQuery] = useState("");
  const [screenResult, setScreenResult] = useState<ScreenResult | null>(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [removingEntry, setRemovingEntry] = useState<RestrictedEntryRow | null>(null);
  const [loggingEvent, setLoggingEvent] = useState(false);
  const [logPrefill, setLogPrefill] = useState<{
    event_type: WallEventType;
    matter: string;
    decision_basis: string;
  } | null>(null);

  const { data: entries, isLoading: entriesLoading } = useQuery({
    queryKey: ["restricted-entries", tenant.id],
    queryFn: () => listRestrictedEntries(tenant.id),
  });
  const { data: events, isLoading: eventsLoading } = useQuery({
    queryKey: ["wall-events", tenant.id],
    queryFn: () => listWallEvents(tenant.id),
  });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  const invalidateEntries = () =>
    void qc.invalidateQueries({ queryKey: ["restricted-entries", tenant.id] });
  const invalidateEvents = () =>
    void qc.invalidateQueries({ queryKey: ["wall-events", tenant.id] });

  const removeEntry = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      removeRestrictedEntry(id, reason, user?.id ?? null),
    onSuccess: () => {
      invalidateEntries();
      setRemovingEntry(null);
    },
  });

  const stats = useMemo(() => {
    const list = entries ?? [];
    return {
      activeRestricted: list.filter((e) => e.status === "active" && e.list_type === "restricted")
        .length,
      activeWatch: list.filter((e) => e.status === "active" && e.list_type === "watch").length,
      removed: list.filter((e) => e.status === "removed").length,
    };
  }, [entries]);

  const wallStats = useMemo(() => wallLogStats(events ?? []), [events]);

  const runScreen = () => {
    const result = screenName(screenQuery, entries ?? []);
    setScreenResult(result);
  };

  const logThisScreen = () => {
    if (!screenResult) return;
    const anyRestricted = screenResult.matches.some((m) => m.entry.list_type === "restricted");
    setLogPrefill({
      event_type: "list_screen",
      matter: screenResult.query,
      decision_basis: screenResult.hit
        ? `Screened "${screenResult.query}" — ${anyRestricted ? "RESTRICTED" : "WATCH"} hit against ${screenResult.matches.map((m) => m.entry.issuer_name).join(", ")}.`
        : `Screened "${screenResult.query}" — no match against the active restricted/watch list.`,
    });
    setLoggingEvent(true);
  };

  return (
    <>
      <PageHeader
        title="Restricted list"
        subtitle="MNPI/information-barrier documentation (Advisers Act §204A) — the restricted and watch lists, and the append-only log every grant, denial, screen, and review is recorded to."
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: "Active restricted", value: stats.activeRestricted, tone: "breach" as const },
          { label: "Active watch", value: stats.activeWatch, tone: "risk" as const },
          { label: "Removed (history)", value: stats.removed, tone: "neutral" as const },
          { label: "Wall events logged", value: wallStats.total, tone: "info" as const },
        ].map((s) => (
          <Card key={s.label} className="px-4 py-3">
            <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-neutral-500">
              <Dot tone={s.tone} /> {s.label}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-50">
              {s.value}
            </div>
          </Card>
        ))}
      </div>

      {/* ------------------------------------------------------ Screen a name */}
      <Card className="p-4">
        <CardHeader
          className="px-0 pb-3 pt-0"
          title="Screen a name"
          action={
            <span className="text-[11px] normal-case tracking-normal text-neutral-600">
              Checks the active restricted/watch list only — rules-based, deterministic
            </span>
          }
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-neutral-500" />
            <input
              value={screenQuery}
              onChange={(e) => setScreenQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runScreen();
              }}
              placeholder="Issuer name or ticker — e.g. “Acme Robotics” or “ACME”"
              className="w-full rounded-md border border-neutral-800 bg-neutral-900/60 py-2 pl-9 pr-2.5 text-sm text-neutral-200 placeholder:text-neutral-600"
            />
          </div>
          <Button
            variant="primary"
            disabled={!screenQuery.trim() || entriesLoading}
            onClick={runScreen}
          >
            <Search className="size-4" /> Screen
          </Button>
        </div>

        {screenResult && (
          <div className="mt-4 space-y-3">
            {!screenResult.hit ? (
              <div className="flex items-center gap-3 rounded-lg border border-emerald-800/60 bg-emerald-950/30 px-4 py-3.5">
                <CheckCircle2 className="size-6 shrink-0 text-emerald-400" />
                <div>
                  <div className="text-base font-semibold text-emerald-300">PASS — no match</div>
                  <div className="text-xs text-emerald-400/80">
                    &ldquo;{screenResult.query}&rdquo; does not match the active restricted or watch
                    list.
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {screenResult.matches.map((m, i) => {
                  const full = entries?.find((e) => e.id === m.entry.id);
                  return (
                    <div
                      key={`${m.entry.id}-${i}`}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border px-4 py-3.5",
                        m.entry.list_type === "restricted"
                          ? "border-red-800/70 bg-red-950/40"
                          : "border-amber-800/70 bg-amber-950/40",
                        i > 0 && "mt-2",
                      )}
                    >
                      <AlertTriangle
                        className={cn(
                          "size-6 shrink-0",
                          m.entry.list_type === "restricted" ? "text-red-400" : "text-amber-400",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <div
                          className={cn(
                            "text-base font-semibold",
                            m.entry.list_type === "restricted" ? "text-red-300" : "text-amber-300",
                          )}
                        >
                          {m.entry.list_type === "restricted" ? "RESTRICTED HIT" : "WATCH HIT"} —{" "}
                          {m.entry.issuer_name}
                        </div>
                        <div className="mt-0.5 text-xs text-neutral-300">{m.entry.reason}</div>
                        <div className="mt-1 text-[11px] text-neutral-500">
                          {full ? SOURCE_LABEL[full.source] : null}
                          {m.entry.ticker ? ` · ${m.entry.ticker}` : ""} · matched on{" "}
                          {m.matchType.replace("issuer_", "issuer ")}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {canLog && (
              <Button size="sm" variant="ghost" onClick={logThisScreen}>
                <ScrollText className="size-3.5" /> Log this screen to the wall-crossing log
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* --------------------------------------------------------- Entries */}
      <Card className="overflow-hidden">
        <CardHeader
          title="Restricted & watch list"
          action={
            canEdit ? (
              <Button variant="primary" size="sm" onClick={() => setAddingEntry(true)}>
                <Plus className="size-4" /> Add to list
              </Button>
            ) : undefined
          }
        />
        {entriesLoading ? (
          <p className="px-4 pb-4 text-sm text-neutral-500">Loading…</p>
        ) : !entries || entries.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert className="size-7" />}
            title="No entries yet"
            description="Add every current board seat, live diligence target, and NDA counterparty to the restricted or watch list — this is the documentation SEC exams look for under §204A."
            action={
              canEdit ? (
                <Button variant="primary" onClick={() => setAddingEntry(true)}>
                  <Plus className="size-4" /> Add to list
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-neutral-800 text-[11px] uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Issuer</th>
                  <th className="px-3 py-2 font-medium">Ticker</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Reason</th>
                  <th className="px-3 py-2 font-medium">Since</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  {canEdit && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-900">
                {entries.map((e) => (
                  <tr key={e.id} className="hover:bg-neutral-900/40">
                    <td className="px-3 py-2.5 text-neutral-200">{e.issuer_name}</td>
                    <td className="px-3 py-2.5 text-neutral-400">{e.ticker ?? "—"}</td>
                    <td className="px-3 py-2.5">
                      <Badge tone={LIST_TYPE_TONE[e.list_type]}>
                        {LIST_TYPE_LABEL[e.list_type]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-neutral-400">{SOURCE_LABEL[e.source]}</td>
                    <td className="max-w-xs px-3 py-2.5 text-neutral-300">
                      <div className="truncate" title={e.reason}>
                        {e.reason}
                      </div>
                      {e.status === "removed" && e.removal_reason && (
                        <div className="mt-0.5 truncate text-[11px] text-neutral-500">
                          Removed: {e.removal_reason}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-neutral-400">
                      {fmtDate(e.effective_at)}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={e.status === "active" ? "clear" : "neutral"}>
                        {e.status === "active" ? "Active" : "Removed"}
                      </Badge>
                    </td>
                    {canEdit && (
                      <td className="px-3 py-2.5 text-right">
                        {e.status === "active" && (
                          <button
                            title="Remove from list"
                            onClick={() => setRemovingEntry(e)}
                            className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-red-400"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ----------------------------------------------- Wall-crossing log */}
      <Card className="overflow-hidden">
        <CardHeader
          title="Wall-crossing log"
          action={
            canLog ? (
              <Button variant="secondary" size="sm" onClick={() => setLoggingEvent(true)}>
                <Plus className="size-4" /> Log event
              </Button>
            ) : undefined
          }
        />
        {eventsLoading ? (
          <p className="px-4 pb-4 text-sm text-neutral-500">Loading…</p>
        ) : !events || events.length === 0 ? (
          <EmptyState
            icon={<ScrollText className="size-7" />}
            title="No wall-crossing events logged yet"
            description="Every grant, denial, access review, and screen should be logged here — timestamped, who authorized it, and why. This append-only log is the §204A exam artifact."
            action={
              canLog ? (
                <Button variant="primary" onClick={() => setLoggingEvent(true)}>
                  <Plus className="size-4" /> Log event
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="divide-y divide-neutral-900">
            {events.map((ev) => {
              const Icon = EVENT_TYPE_ICON[ev.event_type];
              return (
                <div key={ev.id} className="flex items-start gap-3 px-4 py-3">
                  <Icon
                    className={cn(
                      "mt-0.5 size-4 shrink-0",
                      ev.event_type === "wall_crossing_granted" && "text-emerald-400",
                      ev.event_type === "wall_crossing_denied" && "text-red-400",
                      ev.event_type === "access_review" && "text-sky-400",
                      ev.event_type === "list_screen" && "text-neutral-500",
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-neutral-200">{ev.person_name}</span>
                      {ev.person_role && (
                        <span className="text-[11px] text-neutral-500">{ev.person_role}</span>
                      )}
                      <Badge tone={EVENT_TYPE_TONE[ev.event_type]}>
                        {EVENT_TYPE_LABEL[ev.event_type]}
                      </Badge>
                      <span className="ml-auto whitespace-nowrap text-[11px] tabular-nums text-neutral-500">
                        {fmtDateTime(ev.occurred_at)}
                      </span>
                    </div>
                    {ev.matter && (
                      <div className="mt-0.5 text-xs text-neutral-400">Matter: {ev.matter}</div>
                    )}
                    {ev.decision_basis && (
                      <div className="mt-0.5 text-xs text-neutral-300">{ev.decision_basis}</div>
                    )}
                    {ev.approved_by && (
                      <div className="mt-0.5 text-[11px] text-neutral-500">
                        Approved by{" "}
                        {ev.approved_by === user?.id ? "you" : `${ev.approved_by.slice(0, 8)}…`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {addingEntry && (
        <AddEntryDialog
          tenantId={tenant.id}
          funds={funds ?? []}
          addedBy={user?.id ?? null}
          onClose={() => setAddingEntry(false)}
          onDone={() => {
            invalidateEntries();
            setAddingEntry(false);
          }}
        />
      )}

      {removingEntry && (
        <RemoveEntryDialog
          entry={removingEntry}
          isPending={removeEntry.isPending}
          error={removeEntry.error?.message}
          onClose={() => setRemovingEntry(null)}
          onConfirm={(reason) => removeEntry.mutate({ id: removingEntry.id, reason })}
        />
      )}

      {loggingEvent && (
        <LogEventDialog
          tenantId={tenant.id}
          entries={(entries ?? []).filter((e) => e.status === "active")}
          recordedBy={user?.id ?? null}
          prefill={logPrefill}
          onClose={() => {
            setLoggingEvent(false);
            setLogPrefill(null);
          }}
          onDone={() => {
            invalidateEvents();
            setLoggingEvent(false);
            setLogPrefill(null);
          }}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------ ModalShell */

function ModalShell({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4">
      <button
        type="button"
        aria-label="Close dialog"
        tabIndex={-1}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={cn(
          "relative z-10 mt-[8vh] w-full overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950 shadow-2xl shadow-black/60",
          wide ? "max-w-2xl" : "max-w-lg",
        )}
      >
        <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-3">
          <h2 className="text-sm font-medium text-neutral-100">{title}</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-800 hover:text-neutral-200"
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- AddEntryDialog */

function AddEntryDialog({
  tenantId,
  funds,
  addedBy,
  onClose,
  onDone,
}: {
  tenantId: string;
  funds: Fund[];
  addedBy: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [issuer, setIssuer] = useState("");
  const [ticker, setTicker] = useState("");
  const [listType, setListType] = useState<ListType>("restricted");
  const [source, setSource] = useState<EntrySource>("board_seat");
  const [reason, setReason] = useState("");
  const [fundId, setFundId] = useState("");
  const [notes, setNotes] = useState("");

  const create = useMutation({
    mutationFn: () =>
      addRestrictedEntry({
        tenant_id: tenantId,
        issuer_name: issuer.trim(),
        ticker: ticker.trim() ? ticker.trim().toUpperCase() : null,
        list_type: listType,
        reason: reason.trim(),
        source,
        fund_id: fundId || null,
        notes: notes.trim() || null,
        added_by: addedBy,
      }),
    onSuccess: onDone,
  });

  return (
    <ModalShell title="Add to restricted list" onClose={onClose} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Issuer *">
          <input
            className={inputCls}
            value={issuer}
            onChange={(e) => setIssuer(e.target.value)}
            placeholder="e.g. Acme Robotics, Inc."
          />
        </Field>
        <Field label="Ticker">
          <input
            className={inputCls}
            value={ticker}
            onChange={(e) => setTicker(e.target.value)}
            placeholder="Optional"
          />
        </Field>
        <Field label="List type">
          <select
            className={inputCls}
            value={listType}
            onChange={(e) => setListType(e.target.value as ListType)}
          >
            {(Object.keys(LIST_TYPE_LABEL) as ListType[]).map((t) => (
              <option key={t} value={t}>
                {LIST_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Source">
          <select
            className={inputCls}
            value={source}
            onChange={(e) => setSource(e.target.value as EntrySource)}
          >
            {(Object.keys(SOURCE_LABEL) as EntrySource[]).map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Fund (optional)" className="sm:col-span-2">
          <select className={inputCls} value={fundId} onChange={(e) => setFundId(e.target.value)}>
            <option value="">— Firm-wide, not fund-specific —</option>
            {funds.map((f) => (
              <option key={f.id} value={f.id}>
                {f.short_name ?? f.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reason *" className="sm:col-span-2">
          <textarea
            className={cn(inputCls, "min-h-20 resize-y")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this issuer on the list — the documentation an exam will ask for. E.g. “Board seat — J. Okafor is a director.”"
          />
        </Field>
        <Field label="Notes (optional)" className="sm:col-span-2">
          <textarea
            className={cn(inputCls, "min-h-16 resize-y")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
      {create.error && <p className="mt-3 text-xs text-red-300">{create.error.message}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!issuer.trim() || !reason.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? <Spinner className="size-4" /> : <Plus className="size-4" />}
          Add to list
        </Button>
      </div>
    </ModalShell>
  );
}

/* ----------------------------------------------------- RemoveEntryDialog */

function RemoveEntryDialog({
  entry,
  isPending,
  error,
  onClose,
  onConfirm,
}: {
  entry: RestrictedEntryRow;
  isPending: boolean;
  error?: string | undefined;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");

  return (
    <ModalShell title={`Remove ${entry.issuer_name}`} onClose={onClose}>
      <p className="text-xs text-neutral-400">
        This does not delete the entry — it flips the status to &ldquo;removed&rdquo; and keeps the
        full history for the record. A removal reason is required.
      </p>
      <Field label="Removal reason *" className="mt-3">
        <textarea
          className={cn(inputCls, "min-h-20 resize-y")}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. “Position exited; 30-day cooling period observed.”"
        />
      </Field>
      {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={!reason.trim() || isPending}
          onClick={() => onConfirm(reason.trim())}
        >
          {isPending ? <Spinner className="size-4" /> : <Trash2 className="size-4" />}
          Remove from list
        </Button>
      </div>
    </ModalShell>
  );
}

/* --------------------------------------------------------- LogEventDialog */

function LogEventDialog({
  tenantId,
  entries,
  recordedBy,
  prefill,
  onClose,
  onDone,
}: {
  tenantId: string;
  entries: RestrictedEntryRow[];
  recordedBy: string | null;
  prefill: { event_type: WallEventType; matter: string; decision_basis: string } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [personName, setPersonName] = useState("");
  const [personEmail, setPersonEmail] = useState("");
  const [personRole, setPersonRole] = useState("");
  const [eventType, setEventType] = useState<WallEventType>(
    prefill?.event_type ?? "wall_crossing_granted",
  );
  const [entryId, setEntryId] = useState("");
  const [matter, setMatter] = useState(prefill?.matter ?? "");
  const [basis, setBasis] = useState(prefill?.decision_basis ?? "");
  const [selfApproved, setSelfApproved] = useState(false);
  const [notes, setNotes] = useState("");

  const log = useMutation({
    mutationFn: () =>
      recordWallEvent({
        tenant_id: tenantId,
        restricted_entry_id: entryId || null,
        person_name: personName.trim(),
        person_email: personEmail.trim() || null,
        person_role: personRole.trim() || null,
        event_type: eventType,
        matter: matter.trim() || null,
        decision_basis: basis.trim() || null,
        approved_by: selfApproved ? recordedBy : null,
        notes: notes.trim() || null,
        recorded_by: recordedBy,
      }),
    onSuccess: onDone,
  });

  return (
    <ModalShell title="Log wall-crossing event" onClose={onClose} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Person *">
          <input
            className={inputCls}
            value={personName}
            onChange={(e) => setPersonName(e.target.value)}
            placeholder="Who crossed / was screened / was reviewed"
          />
        </Field>
        <Field label="Event type">
          <select
            className={inputCls}
            value={eventType}
            onChange={(e) => setEventType(e.target.value as WallEventType)}
          >
            {(Object.keys(EVENT_TYPE_LABEL) as WallEventType[]).map((t) => (
              <option key={t} value={t}>
                {EVENT_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Person email (optional)">
          <input
            className={inputCls}
            value={personEmail}
            onChange={(e) => setPersonEmail(e.target.value)}
          />
        </Field>
        <Field label="Person role (optional)">
          <input
            className={inputCls}
            value={personRole}
            onChange={(e) => setPersonRole(e.target.value)}
            placeholder="e.g. deal_team, ir, analyst"
          />
        </Field>
        <Field label="Related restricted-list entry (optional)" className="sm:col-span-2">
          <select className={inputCls} value={entryId} onChange={(e) => setEntryId(e.target.value)}>
            <option value="">— None —</option>
            {entries.map((e) => (
              <option key={e.id} value={e.id}>
                {e.issuer_name}
                {e.ticker ? ` (${e.ticker})` : ""} — {LIST_TYPE_LABEL[e.list_type]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Matter" className="sm:col-span-2">
          <input
            className={inputCls}
            value={matter}
            onChange={(e) => setMatter(e.target.value)}
            placeholder="Deal / issuer context"
          />
        </Field>
        <Field label="Decision basis" className="sm:col-span-2">
          <textarea
            className={cn(inputCls, "min-h-20 resize-y")}
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder="Why granted/denied, or the review's outcome — this is the exam documentation."
          />
        </Field>
        <Field label="Notes (optional)" className="sm:col-span-2">
          <textarea
            className={cn(inputCls, "min-h-16 resize-y")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Field>
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-neutral-400">
        <input
          type="checkbox"
          checked={selfApproved}
          onChange={(e) => setSelfApproved(e.target.checked)}
          className="accent-brand-500"
        />
        I am the approver of record for this event
      </label>
      {log.error && <p className="mt-3 text-xs text-red-300">{log.error.message}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!personName.trim() || log.isPending}
          onClick={() => log.mutate()}
        >
          {log.isPending ? <Spinner className="size-4" /> : <Plus className="size-4" />}
          Log event
        </Button>
      </div>
    </ModalShell>
  );
}

export function RestrictedListPage() {
  return (
    <TenantLayout>
      <RestrictedListInner />
    </TenantLayout>
  );
}
